import { createFileRoute, Link } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { LANGUAGE_LABELS, LANGUAGE_OPTIONS } from '@/admin/languages';
import { PersonRowActions } from '@/admin/people/PersonRowActions';
import { peopleFilterQuery } from '@/admin/people/peopleFilters';
import {
  genderLabel,
  PERSON_TYPES,
  profileTypeLabel,
} from '@/admin/people/profileTypes';
import { tr, usePeopleT } from '@/admin/people/strings';
import { Badge } from '@/admin/ui/Badge';
import { createTablePage } from '@/admin/ui/createTablePage';
import {
  readTableSearch,
  type TableFilters,
  writeTableSearch,
} from '@/admin/ui/tableSearch';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import i18n from '~/i18n';
import type { FilterField } from '~/styles/tables/filterandSettingsStyles';

type AdminPersonListItem = components['schemas']['AdminPersonListItem'];

// Legacy's admin datatables page twenty at a time.
const PAGE_SIZE = 20;
const EMPTY = '—';

/** The table's own URL state, kept so a link can seed a filter. */
type PeopleSearch = {
  page?: number;
  q?: string;
  sort?: string;
  order?: 'asc' | 'desc';
  filters?: TableFilters;
};

const columnHelper = createColumnHelper<AdminPersonListItem>();

function useCountryNames(): Map<string, string> {
  const { data } = api.getCountries.useQuery({});
  return new Map((data?.countries ?? []).map((c) => [c.code, c.name]));
}

function NameCell({ person }: { person: AdminPersonListItem }) {
  const t = usePeopleT();
  return (
    <Cell>
      <span>{person.display_name}</span>
      {person.suspended && (
        <Badge $variant="error">{t('admin.people.badge.suspended')}</Badge>
      )}
      {person.dormant && (
        <Badge $variant="neutral">{t('admin.people.badge.dormant')}</Badge>
      )}
      {person.is_alias && (
        <Badge $variant="neutral">{t('admin.people.badge.alias')}</Badge>
      )}
      {person.pending_deletion && (
        <Badge $variant="warning">
          {person.pending_deletion.held
            ? t('admin.people.badge.deletionHeld')
            : t('admin.people.badge.pendingDeletion')}
        </Badge>
      )}
    </Cell>
  );
}

function LocationCell({ person }: { person: AdminPersonListItem }) {
  const countries = useCountryNames();
  const place = [person.city, person.region].filter(Boolean).join(', ');
  const country = person.country
    ? (countries.get(person.country) ?? person.country)
    : '';
  return <>{[place, country].filter(Boolean).join(' ') || EMPTY}</>;
}

/**
 * Helpers list the languages they teach, with `*` on the site language when
 * there are several; a student shows the one they learn.
 */
function languageCell(person: AdminPersonListItem): string {
  if (person.languages.length === 0) return EMPTY;
  return person.languages
    .map((code) => {
      const label = i18n.t(LANGUAGE_LABELS[code]);
      return person.languages.length > 1 && code === person.language
        ? `${label}*`
        : label;
    })
    .join(', ');
}

const columns = [
  columnHelper.accessor('id', {
    id: 'id',
    header: () => tr('admin.people.col.id'),
  }),
  columnHelper.display({
    id: 'name',
    header: () => tr('admin.people.col.name'),
    cell: ({ row }) => <NameCell person={row.original} />,
  }),
  columnHelper.accessor((row) => profileTypeLabel(row.profile_type), {
    id: 'profile_type',
    header: () => tr('admin.people.col.type'),
  }),
  columnHelper.display({
    id: 'location',
    header: () => tr('admin.people.col.location'),
    cell: ({ row }) => <LocationCell person={row.original} />,
  }),
  columnHelper.display({
    id: 'language',
    header: () => tr('admin.people.col.language'),
    cell: ({ row }) => languageCell(row.original),
  }),
  columnHelper.accessor((row) => genderLabel(row.gender), {
    id: 'gender',
    header: () => tr('admin.people.col.gender'),
  }),
  columnHelper.accessor((row) => row.age ?? '--', {
    id: 'age',
    header: () => tr('admin.people.col.age'),
  }),
  columnHelper.display({
    id: 'actions',
    header: () => tr('admin.people.col.actions'),
    cell: ({ row }) => <PersonRowActions person={row.original} />,
  }),
];

/** Legacy's seventeen Users List filters; each key IS the wire parameter. */
function usePeopleFilterFields(): FilterField[] {
  const { t } = useTranslation();
  const { data } = api.getCountries.useQuery({});
  const countries = data?.countries ?? [];
  const text = (key: string, label: Parameters<typeof tr>[0]): FilterField => ({
    key,
    label: tr(label),
    type: 'text',
  });
  return [
    text('display_name', 'admin.people.filters.displayName'),
    text('first_name', 'admin.people.filters.firstName'),
    text('last_name', 'admin.people.filters.lastName'),
    text('user_id', 'admin.people.filters.userId'),
    {
      key: 'profile_type',
      label: tr('admin.people.col.type'),
      type: 'multiselect',
      options: PERSON_TYPES.map((value) => ({
        value,
        label: profileTypeLabel(value),
      })),
    },
    text('email', 'admin.people.filters.email'),
    {
      key: 'registered_after',
      label: tr('admin.people.filters.registeredAfter'),
      type: 'date',
      groupWith: 'registered_before',
    },
    {
      key: 'registered_before',
      label: tr('admin.people.filters.registeredBefore'),
      type: 'date',
    },
    {
      key: 'country',
      label: tr('admin.people.filters.country'),
      type: 'multiselect',
      options: countries.map((c) => ({ value: c.code, label: c.name })),
    },
    text('city', 'admin.people.filters.city'),
    text('state', 'admin.people.filters.state'),
    text('zip', 'admin.people.filters.zip'),
    {
      key: 'language',
      label: tr('admin.people.filters.language'),
      type: 'multiselect',
      options: LANGUAGE_OPTIONS.map(([value, labelKey]) => ({
        value,
        label: t(labelKey),
      })),
    },
    // Rendered by the helper picker (`helperPickerKeys`).
    {
      key: 'study_helper_id',
      label: tr('admin.people.filters.studyHelper'),
      type: 'multiselect',
    },
    {
      key: 'suspended',
      label: tr('admin.people.filters.suspended'),
      type: 'select',
      options: [
        { value: 'true', label: tr('admin.people.filters.suspendedYes') },
        { value: 'false', label: tr('admin.people.filters.suspendedNo') },
      ],
    },
    text('phone', 'admin.people.filters.phone'),
    text('whatsapp', 'admin.people.filters.whatsapp'),
  ];
}

function AddPostalStudent() {
  const t = usePeopleT();
  // Postal students are created inside a group (`create_postal_student` is
  // per group), so the shortcut goes to Groups.
  return <Link to="/admin/groups/">{t('admin.people.addPostalStudent')}</Link>;
}

const PeopleList = createTablePage<
  AdminPersonListItem,
  '/admin/people/$userId'
>({
  name: 'admin.nav.people',
  pageSize: PAGE_SIZE,
  searchable: true,
  searchPlaceholder: 'admin.people.searchPlaceholder',
  urlState: true,
  defaultSort: { key: 'id', order: 'desc' },
  // An unrecognised sort key is ignored silently server-side, so these
  // spellings are the wire contract. Location, language and gender are
  // unsortable, as in legacy.
  sortKeys: {
    id: 'id',
    name: 'display_name',
    profile_type: 'profile_type',
    age: 'birth_year',
  },
  filterFields: usePeopleFilterFields,
  helperPickerKeys: ['study_helper_id'],
  useData: (_page, search, { paging, sort, filters }) =>
    api.searchPeople.useQuery({
      query: {
        limit: paging.limit,
        offset: paging.offset,
        ...(search ? { q: search } : {}),
        ...(sort ? { sort: sort.key, order: sort.order } : {}),
        ...peopleFilterQuery(filters),
      },
    }),
  columns,
  itemBaseUrl: '/admin/people/$userId',
  getParams: (row) => ({ userId: String(row.id) }),
  actions: <AddPostalStudent />,
});

export const Route = createFileRoute('/admin/people/')({
  staticData: { minTier: 'FRONT_END_SUPPORT' },
  // Passes the whole table state through (not just page and q) so a redirect
  // can seed a filter.
  validateSearch: (raw: Record<string, unknown>): PeopleSearch => {
    const out = writeTableSearch({}, readTableSearch(raw)) as PeopleSearch;
    // A sort switched off survives a reload as an empty key.
    if (raw.sort === '') out.sort = '';
    return out;
  },
  component: PeopleList,
});

const Cell = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;
